import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink, Globe } from "lucide-react";
import { GlowButton } from "@/components/GlowButton";
import { GlassCard } from "@/components/GlassCard";
import { SectionHeader } from "@/components/SectionHeader";
import { useSeo } from "@/hooks/useSeo";
import { WORK } from "@/lib/data";

const container = "mx-auto max-w-6xl px-4 sm:px-6 lg:px-8";
const VP_20 = { once: true, amount: 0.2 };

// Auto-generate a screenshot preview from the site URL (thum.io), unless an
// explicit `image` is provided on the project. Zero-effort for new projects —
// just add a `url` and a live preview appears.
const previewFor = (p) =>
  p.image || `https://image.thum.io/get/width/1200/crop/900/noanimate/${p.url}`;

// Clean the URL for a compact display label on the card.
const hostLabel = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

function ProjectCard({ p, i }) {
  const [t, setT] = React.useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ x: px * 4, y: -py * 4 });
  };
  const reset = () => setT({ x: 0, y: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VP_20}
      transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
    >
      <GlassCard
        glow={i % 2 ? "purple" : "green"}
        className="flex h-full flex-col overflow-hidden"
        style={{ transform: `perspective(900px) rotateY(${t.x}deg) rotateX(${t.y}deg)` }}
        onMouseMove={onMove}
        onMouseLeave={reset}
        data-testid={`work-card-${p.id}`}
      >
        {/* Preview image */}
        <div className="relative h-56 overflow-hidden sm:h-60">
          <img
            src={previewFor(p)}
            alt={`${p.name} website preview`}
            loading="lazy"
            className="h-full w-full object-cover object-top opacity-85 transition-transform duration-700 hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,11,13,0.94)] via-[rgba(11,11,13,0.15)] to-transparent" />
          <span
            className="absolute left-3 top-3 rounded-full glass px-2.5 py-1 text-[10px] uppercase tracking-wider text-neon-g"
            data-testid={`work-category-${p.id}`}
          >
            {p.category}
          </span>
          <span className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full glass px-2.5 py-1 text-[10px] text-muted-brand">
            <Globe size={11} className="text-neon-g" /> {hostLabel(p.url)}
          </span>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-display text-lg font-semibold text-white" data-testid={`work-name-${p.id}`}>
            {p.name}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-brand">{p.description}</p>

          {/* Technologies */}
          <div className="mt-5">
            <div className="mb-2 text-[11px] uppercase tracking-[0.14em] text-neon-g">Technologies</div>
            <div className="flex flex-wrap gap-2" data-testid={`work-tech-${p.id}`}>
              {p.technologies.map((tech) => (
                <span
                  key={tech}
                  className="rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.02)] px-2.5 py-1 text-[11px] text-muted-brand"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Visit Website */}
          <div className="mt-6 pt-1">
            <GlowButton
              as="a"
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              className="w-full"
              data-testid={`work-visit-${p.id}`}
            >
              Visit Website <ExternalLink size={15} />
            </GlowButton>
          </div>
        </div>
      </GlassCard>
    </motion.div>
  );
}

export default function OurWork() {
  useSeo({
    title: "Our Work — Métis Tech Labs",
    description:
      "Real client websites and digital systems built by Métis Tech Labs — modern, responsive, and engineered for engagement and growth.",
  });

  return (
    <div className="pt-32">
      <section className={container}>
        <SectionHeader
          eyebrow="Our Work"
          title="Projects we've brought to life"
          subtitle="A selection of real client websites and digital systems we've designed, built, and launched."
        />
      </section>

      {/* PROJECT GRID */}
      <section className={`${container} mt-16`}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="work-grid">
          {WORK.map((p, i) => (
            <ProjectCard key={p.id} p={p} i={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`${container} mb-24 mt-20`}>
        <div className="neon-frame relative overflow-hidden rounded-3xl glass-strong px-6 py-12 text-center sm:px-12">
          <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            Ready to build something exceptional?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-muted-brand">
            Let's craft a modern, high-performing digital presence for your business.
          </p>
          <div className="mt-7 flex justify-center">
            <GlowButton as={Link} to="/contact" pulse data-testid="work-cta-button">
              Start your project <ArrowUpRight size={16} />
            </GlowButton>
          </div>
        </div>
      </section>
    </div>
  );
}
